import { useState, useContext, useId } from "react";
import { PageSeoHead } from "@/components/seo/PageSeoHead";
import { ROBOTS_NOINDEX } from "@/lib/siteSeo";
import Image from "next/image";
import Link from "next/link";
import {
  Flex,
  Text,
  Center,
  Button,
  IconButton,
  Progress,
  Box,
} from "@chakra-ui/react";
import { ViewIcon, ViewOffIcon } from "@chakra-ui/icons";
import { motion } from "framer-motion";
import { FiUser, FiMail, FiLock, FiPhone } from "react-icons/fi";

import { EnhancedInput } from "../../components/enhanced-input";
import { AuthContext } from "../../context/AuthContext";
import { canSSRGuest } from "../../utils/canSSRGuest";
import {
  showSuccessToast,
  showErrorToast,
  showWarningToast
} from "../../utils/toastifyConfig";
import { validateEmail, validateName } from "@/utils/validators";
import {
  PHONE_REQUIRED_EMPTY_MESSAGE,
  PHONE_REQUIRED_INVALID_MESSAGE,
  validatePhoneFormat,
} from "@/utils/phoneValidationMessages";

export default function Register() {
  const { signUp } = useContext(AuthContext);

  // Gerar IDs estáveis com useId do React
  const idPrefix = useId();
  const nameId = `${idPrefix}-name`;
  const emailId = `${idPrefix}-email`;
  const phoneId = `${idPrefix}-phone`;
  const passwordId = `${idPrefix}-password`;

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");

  // Função para formatar o número de telefone com máscara
  const formatPhone = (value: string) => {
    // Remove todos os caracteres não numéricos
    const numbers = value.replace(/\D/g, "");

    // Aplica a máscara de acordo com o tamanho do número
    if (numbers.length <= 10) {
      // Formato para telefone fixo: (99) 9999-9999
      if (numbers.length <= 2) {
        return numbers.length === 0 ? "" : `(${numbers}`;
      } else if (numbers.length <= 6) {
        return `(${numbers.slice(0, 2)}) ${numbers.slice(2)}`;
      } else {
        return `(${numbers.slice(0, 2)}) ${numbers.slice(2, 6)}-${numbers.slice(6, 10)}`;
      }
    } else {
      // Formato para celular: (99) 99999-9999
      if (numbers.length <= 2) {
        return `(${numbers}`;
      } else if (numbers.length <= 7) {
        return `(${numbers.slice(0, 2)}) ${numbers.slice(2)}`;
      } else {
        return `(${numbers.slice(0, 2)}) ${numbers.slice(2, 7)}-${numbers.slice(7, 11)}`;
      }
    }
  };
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [nameError, setNameError] = useState("");
  const [emailError, setEmailError] = useState("");
  const [phoneError, setPhoneError] = useState("");
  const [passwordStrength, setPasswordStrength] = useState(0);

  const calculatePasswordStrength = (password: string) => {
    let strength = 0;
    if (password.length >= 6) strength += 25;
    if (password.match(/[A-Z]/)) strength += 25;
    if (password.match(/[0-9]/)) strength += 25;
    if (password.match(/[^A-Za-z0-9]/)) strength += 25;
    setPasswordStrength(strength);
  };

  const getPasswordStrengthColor = () => {
    if (passwordStrength <= 25) return "red.500";
    if (passwordStrength <= 50) return "orange.500";
    if (passwordStrength <= 75) return "yellow.500";
    return "green.500";
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleRegister();
    }
  };

  // Função para formatar o telefone para o formato correto antes de enviar para o backend
  const formatPhoneForSubmission = (phoneWithMask: string): string => {
    // Remove todos os caracteres não numéricos
    return phoneWithMask.replace(/\D/g, "");
  };

  async function handleRegister() {
    setNameError("");
    setEmailError("");
    setPhoneError("");

    if (name === "" || email === "" || password === "" || phone === "") {
      showWarningToast("Preencha todos os campos obrigatórios.", "Atenção!");
      if (!phone.trim()) {
        setPhoneError(PHONE_REQUIRED_EMPTY_MESSAGE);
      }
      return;
    }

    if (!validateName(name)) {
      setNameError("O nome deve ter pelo menos 3 caracteres");
      return;
    }

    if (!validateEmail(email)) {
      setEmailError("Por favor, insira um email válido");
      return;
    }

    const phoneValidationError = validatePhoneFormat(phone);
    if (phoneValidationError) {
      setPhoneError(phoneValidationError);
      return;
    }

    if (password.length < 6) {
      showErrorToast(
        "A senha deve ter pelo menos 6 caracteres",
        "Senha muito curta"
      );
      return;
    }

    try {
      setIsLoading(true);

      const formattedPhone = formatPhoneForSubmission(phone);

      await signUp({
        name,
        email,
        password,
        phone: formattedPhone,
      });

      showSuccessToast(
        "Redirecionando para o login...",
        "Cadastro realizado!"
      );
      setTimeout(() => {
        window.location.href = "/login";
      }, 1500);
    } catch (error: unknown) {
      const errorMessage =
        error && typeof error === 'object' && 'response' in error &&
          error.response && typeof error.response === 'object' && 'data' in error.response &&
          error.response.data && typeof error.response.data === 'object' && 'error' in error.response.data ?
          String(error.response.data.error) : "Erro ao realizar cadastro";
      showErrorToast(errorMessage, "Erro no cadastro");
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <>
      <PageSeoHead
        title="Cria sua conta no Meu Barbeiro PRO"
        path="/register"
        robots={ROBOTS_NOINDEX}
      />
      <Flex
        background={"barber.900"}
        minHeight="100vh"
        alignItems="center"
        justifyContent="center"
        py={8}
        position="relative"
      >
        <Box
          as={motion.div}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transitionDuration="0.5s"
          width={{ base: "90%", md: "450px" }}
          p={{ base: 6, md: 8 }}
          rounded="xl"
          bg={"barber.900"}
          borderWidth="1px"
          borderColor={"whiteAlpha.100"}
          boxShadow="0 25px 50px -12px rgba(0, 0, 0, 0.8)"
          position="relative"
          overflow="hidden"
          zIndex={1}
        >
          <Box // Linha gradiente no topo
            position="absolute"
            top="0"
            left="0"
            right="0"
            h="4px"
            bgGradient="linear(90deg, #ffb110 0%, #ff9900 100%)"
            boxShadow="0 0 10px rgba(255,177,16,0.6)"
          />
          <Center p={{ base: 4, md: 6 }} mb={6}>
            <Image
              src="/images/logo.png"
              quality={100}
              width={240}
              height={60}
              objectFit="contain"
              alt="Logo Meu Barbeiro PRO"
              style={{ background: "none" }}
            />
          </Center>
          {/* Nome da barbearia */}
          <EnhancedInput
            id={nameId}
            label="Nome da barbearia"
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              setNameError("");
            }}
            placeholder="Nome da sua barbearia"
            type="text"
            isInvalid={!!nameError}
            errorMessage={nameError}
            leftIcon={FiUser}
            onKeyDown={handleKeyDown}
            isRequired
          />
          {/* Email */}
          <EnhancedInput
            id={emailId}
            label="Email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              setEmailError("");
            }}
            placeholder="exemplo@email.com"
            type="email"
            isInvalid={!!emailError}
            errorMessage={emailError}
            leftIcon={FiMail}
            autoComplete="email"
            onKeyDown={handleKeyDown}
            isRequired
          />
          {/* Telefone com máscara aplicada manualmente */}
          <EnhancedInput
            id={phoneId}
            label="Telefone (com DDD)"
            value={phone}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              // Aplicar a formatação e atualizar o estado
              setPhone(formatPhone(e.target.value));
              setPhoneError("");
            }}
            placeholder="Ex: (99) 99999-9999"
            type="tel"
            isInvalid={!!phoneError}
            errorMessage={phoneError}
            leftIcon={FiPhone}
            autoComplete="tel"
            helperText="Celular: (99) 99999-9999 | Fixo: (99) 9999-9999"
            onKeyDown={handleKeyDown}
            maxLength={16} // Tamanho máximo para "(99) 99999-9999"
            isRequired
          />
          {/* Senha */}
          <EnhancedInput
            id={passwordId}
            label="Senha"
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              calculatePasswordStrength(e.target.value);
            }}
            placeholder="Sua senha"
            type={showPassword ? "text" : "password"}
            leftIcon={FiLock}
            onKeyDown={handleKeyDown}
            rightElement={
              <IconButton
                h="1.75rem"
                size="sm"
                bg="transparent"
                aria-label={showPassword ? "Ocultar senha" : "Mostrar senha"}
                icon={showPassword ? <ViewOffIcon /> : <ViewIcon />}
                onClick={() => setShowPassword(!showPassword)}
                _hover={{ bg: "rgba(255,255,255,0.1)" }}
                color="gray.400"
                isDisabled={isLoading}
                tabIndex={-1}
              />
            }
            isRequired
          />
          {/* Barra de força da senha */}
          {password && (
            <Progress
              value={passwordStrength}
              colorScheme={getPasswordStrengthColor()}
              size="xs"
              mt={2}
              borderRadius="full"
            />
          )}
          {/* Botão Criar minha conta */}
          <Button
            variant="solid"
            colorScheme="orange"
            size="lg"
            w="100%"
            height="56px"
            fontSize="md"
            fontWeight="bold"
            color="gray.800"
            mt={6}
            onClick={handleRegister}
            isLoading={isLoading}
            loadingText="Cadastrando..."
            leftIcon={<FiLock />}
          >
            Criar minha conta
          </Button>
          <Center mt={4} color={"white"}>
            <Link href="/login">
              <Text
                cursor="pointer"
                fontSize="sm"
                _hover={{ color: "#ffb110" }}
                transition="all 0.2s"
              >
                Já possui uma conta?{" "}
                <Text
                  as="span"
                  color="#ffb110"
                  fontWeight="bold"
                  textDecor="underline"
                >
                  Faça login
                </Text>
              </Text>
            </Link>
          </Center>
        </Box>
      </Flex>
    </>
  );
}

export const getServerSideProps = canSSRGuest(async (_ctx) => {
  return {
    props: {},
  };
});
